import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';

import { addCartProduct } from '@/redux/slices/cart.slice';
import { addAlert } from '@/redux/slices/primeur.slice';

import { useAppDispatch, useAppSelector } from '@/redux';
import { getProductImage } from '@/utils/productUtils';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import SelectDropdown from '@/components/common/SelectDropdown';
import ProductPrice from '@/components/product/ProductPrice';

import styles from './AddProductToCart.module.scss';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { URLS } from '@/constants/urls';

const AddProductToCart = ({ product, currentItem, quantity, setQuantity, isAlert }) => {
  const { t } = useTranslation();
  const primeurUser = useAppSelector(({ primeur }) => primeur.primeurUser);
  const storeConf = useAppSelector(({ root }) => root.storeConf);

  const dispatch = useAppDispatch();

  const { partnumber, shortdesc, stock, image: imageItem } = currentItem;
  const isAboveTablet = useScreenSizeContext().isTablet;

  const quantities = useMemo(() => {
    return stock ? Array(stock).fill(1).map((_, i) => ({ value: i + 1, label: i + 1 })) : [];
  }, [partnumber]);

  const addToCart = () => {
    dispatch(addCartProduct({
      item: partnumber,
      qty: quantity.value,
      options: {
        set: false
      },
      error: undefined
    }));
    setQuantity({ value: 1, label: 1 });
  };

  let buttonLabel;
  let buttonProps;

  if (isAlert) {
    const isAlerted = primeurUser?.alerts?.includes(product?.partnumber);
    buttonLabel = isAlerted ? t('alert.unsubscribe') : t('alert.alertMe');
    buttonProps = {
      id: 'alert-me',
      kind: isAlerted ? 'secondary' : 'primary',
      onClick: () => dispatch(addAlert({
        alerts: [product?.partnumber],
        name: product?.productName || product?.name,
        isAllChecked: undefined,
        storeId: storeConf.storeId
      })),
      icon: {
        url: isAlerted ? URLS.CROSS_BLACK : URLS.NOTIFICATION_WHITE,
        alt: 'alert'
      },
      fullWidth: true
    };
  } else {
    buttonLabel = t('product.addToCart');
    buttonProps = {
      disabled: stock === 0,
      onClick: addToCart,
      id: 'add-to-cart',
      fullWidth: true
    };
  }

  return (
    <div className={styles.container} data-rbf="add-to-card-modal">
      {isAboveTablet && (
        <img
          className={styles.img}
          src={getProductImage(imageItem ? imageItem : product.image, false, 'mobile')}
          alt={product.productName}
          loading="lazy"
        />
      )}
      <div className={styles.product}>
        {isAboveTablet && (
          <div className={styles.desc} data-test-id="product-description">
            <p className={styles.title}>{product.productName}</p>
            <p>{shortdesc}</p>
          </div>
        )}
        {!isAlert && currentItem && (
          <ProductPrice
            currentItem={currentItem}
            product={product}
            displayUnitBloc={false}
            isMobile={!isAboveTablet}
            customClass={styles.price}
            showVat={false}
          />
        )}
      </div>
      <div className={!isAlert ? styles.selector : styles['alert-button']}>
        {isAboveTablet && (
          <SelectDropdown
            selected={quantity}
            options={quantities}
            onChange={setQuantity}
            disabled={stock === 0}
            className={styles['select-dropdown']}
          />
        )}
        <CallToActionContainer {...buttonProps} >
          {buttonLabel}
        </CallToActionContainer>
      </div>
    </div>
  );
};

AddProductToCart.propTypes = {
  product: PropTypes.object.isRequired,
  currentItem: PropTypes.object.isRequired,
  quantity: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.number
  }).isRequired,
  setQuantity: PropTypes.func.isRequired
};

export default AddProductToCart;
